import { functionalUpdate } from '@tanstack/react-table';
import type { ScrollToOptions } from '@tanstack/react-virtual';
import {
  useEffect,
  useRef,
  type PointerEvent,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { _mergeProps } from '@dynatrace/strato-components/core';

import { dataTableV2InteractiveRowCSS } from './RowInteractivity.sty.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../../../core/styles/useFocusRing.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import type {
  Row,
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * Event that caused a row to become active (e.g. mouse click).
 * @public
 */
export type DataTableV2RowActivationEvent =
  | ReactMouseEvent<HTMLElement>
  | ReactKeyboardEvent<HTMLElement>
  | ReactFocusEvent<HTMLElement>;

/**
 * @public
 */
export interface DataTableV2RowInteractivityBaseProps {
  /**
   * Enables row interactivity.
   *
   * @defaultValue false
   */
  interactiveRows?:
    | boolean
    | {
        /**
         * The `autoActivate` setting controls whether a focused row immediately activates it,
         * making stepping through interactive rows with a keyboard more accessible.
         * @defaultValue true
         */
        autoActivate: boolean;
      };

  /**
   * Callback triggered when active row is changed.
   * @param activeRow - the active row's ID.
   * @param event - the event that caused a row to become active.
   */
  onActiveRowChange?: (
    activeRow: string | null,
    event?: DataTableV2RowActivationEvent,
  ) => void;
}

/**
 * @public
 */
export interface DataTableV2RowInteractivityControlledProps
  extends DataTableV2RowInteractivityBaseProps {
  /**
   * Lets you control the active row.
   **/
  activeRow?: string | null;
  defaultActiveRow?: never;
}

/**
 * @public
 */
export interface DataTableV2RowInteractivityUncontrolledProps
  extends DataTableV2RowInteractivityBaseProps {
  /**
   * Lets you set the initially active row.
   **/
  defaultActiveRow?: string | null;
  activeRow?: never;
}

/**
 * Options for DataTableV2 row interactivity
 * @public
 */
export type DataTableV2RowInteractivityProps =
  | DataTableV2RowInteractivityControlledProps
  | DataTableV2RowInteractivityUncontrolledProps;

/**
 * Extension interface for the table options
 * @internal
 */
export interface DataTableV2RowInteractivityOptions {
  enableRowInteractivity?: boolean;

  onActiveRowChange?: (
    updater: Updater<string | null>,
    event?: DataTableV2RowActivationEvent,
  ) => void;

  enableAutoActivation?: boolean;
}

/**
 * Extension interface for the table state
 * @internal
 */
export interface DataTableV2RowInteractivityState {
  activeRow?: string | null;
}

/**
 * @internal
 */
export interface DataTableV2RowInteractivityInstance {
  /**
   * Updates the active row state of the table via an update function or value.
   */
  setActiveRow: (
    updater: Updater<string | null>,
    event?: DataTableV2RowActivationEvent,
  ) => void;

  /**
   * Updates the currently hovered row.
   */
  setHoveredRow: (rowId: string | null) => void;

  /**
   * Gets the currently hovered row.
   */
  getHoveredRow: () => string | null;

  /**
   * Gets the interactive row event handlers on the table level.
   */
  getInteractiveRowMouseOverHandler: () => (
    event: ReactMouseEvent<HTMLDivElement>,
  ) => void;
  /**
   * Gets the interactive row event handlers on the table level.
   */
  getInteractiveRowMouseLeaveHandler: () => (
    event: ReactMouseEvent<HTMLDivElement>,
  ) => void;
  /**
   * Gets the interactive row event handlers on the table level.
   */
  getInteractiveRowClickHandler: () => (
    event: ReactMouseEvent<HTMLDivElement>,
  ) => void;
}

/**
 * Extension interface to extend the types of the `Row` from the tanstack table.
 * @internal
 */
export interface DataTableV2RowInteractivityRow {
  /**
   * Returns a boolean value if the row can be activated.
   */
  getCanActivateRow: () => boolean;

  /**
   * Returns a boolean value if the row is currently activated.
   */
  getIsActivated: () => boolean;
  /**
   * Activates the row, if it is not activated.
   * Deactivates the row, if it is currently activated.
   */
  toggleActivated: (event?: DataTableV2RowActivationEvent) => void;
}

function removeHoverStyles(target: HTMLElement | SVGElement): void {
  const virtualizationContainer = target.closest(
    '.strato-table-virtualization-container',
  );

  if (virtualizationContainer) {
    const hoveredRows = virtualizationContainer.querySelectorAll(
      `.${dataTableV2InteractiveRowCSS.classNames.variants.hovered.true}`,
    );
    for (const hoveredRow of Array.from(hoveredRows)) {
      hoveredRow.classList.remove(
        dataTableV2InteractiveRowCSS.classNames.variants.hovered.true,
      );
    }
  }
}

function addHoverStyles(row: HTMLElement): void {
  row.classList.add(
    dataTableV2InteractiveRowCSS.classNames.variants.hovered.true,
  );
}

/**
 * Provides all necessary props for an interactive row element.
 *
 * @param row - the table row element
 * @param scrollToIndex - rowVirtualizer function for scrolling to the row with the specified index
 * @returns className and props for the row element
 */
export function useRowInteractivityProps<TData>(
  row: Row<TData>,
  scrollToIndex: (index: number, options?: ScrollToOptions) => void,
): {
  rowInteractivityClassName: string;
  rowInteractivityProps: Record<string, unknown> | undefined;
} {
  const table = useDataTableV2();
  const intl = useIntl();
  const clicked = useRef(false);
  const { isFocusVisible, focusProps } = useFocusRing();

  if (!row.getCanActivateRow()) {
    return { rowInteractivityClassName: '', rowInteractivityProps: undefined };
  }

  const isActivated = row.getIsActivated();
  const isAutoActivationEnabled = table.options.enableAutoActivation;
  const rowInteractivityClassName = dataTableV2InteractiveRowCSS({
    hovered: table.getHoveredRow() === row.id,
    active: isActivated,
    focus: isFocusVisible,
    contained: table.getVisualVariantOptions().contained,
  });

  const rowInteractivityProps = {
    tabIndex: 0,
    'data-trigger': 'interactive-row',
    'aria-label': intl.formatMessage(
      {
        defaultMessage:
          '{isActivated, select, true {Deactivate} other {Activate}} row with id "{rowId}".',
        description: 'Aria label for the interactive row trigger button.',
        id: 'an4qwOaAQd8t60m5',
      },
      {
        isActivated,
        rowId: row.id,
      },
    ),
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      clicked.current = true;
      const { target } = event;

      // main (left button) or auxiliary button (wheel/middle button)
      const isValidMouseButton = event.button === 0 || event.button === 1;

      if (
        !isValidMouseButton ||
        table.options.enableRowInteractivity !== true ||
        (target instanceof HTMLElement === false &&
          target instanceof SVGElement === false)
      ) {
        return;
      }

      const rowElement = target.closest('[role="row"]');
      const hitInteractiveElement = checkIfHitInteractiveElement(
        rowElement,
        target,
      );

      if (!hitInteractiveElement) {
        row.toggleActivated(event);
      }
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      const { key } = event;

      if (key === 'Enter' && !isAutoActivationEnabled) {
        event.preventDefault();
        event.stopPropagation();
        row.toggleActivated(event);
        return;
      }

      let offset: number | null = null;
      switch (key) {
        case 'ArrowDown': {
          offset = +1;
          break;
        }
        case 'ArrowUp': {
          offset = -1;
          break;
        }
        case 'PageUp': {
          offset = -10;
          break;
        }
        case 'PageDown': {
          offset = +10;
          break;
        }
        case 'Home': {
          offset = -Infinity;
          break;
        }
        case 'End': {
          offset = Infinity;
          break;
        }
      }

      if (offset !== null) {
        event.preventDefault();
        event.stopPropagation();
        if (event.target instanceof HTMLElement) {
          const allRows = table.getRowModel().rows;
          // Find the position of the current row in the list
          const currentRowIndex = allRows.findIndex(
            (currentRow) => currentRow.id === row.id,
          );

          if (currentRowIndex !== -1) {
            const clampedIndex = Math.max(
              0,
              Math.min(currentRowIndex + offset, allRows.length - 1),
            );

            const nextRow = allRows.at(clampedIndex);
            if (nextRow !== undefined) {
              scrollToIndex(clampedIndex, {
                align: 'auto',
              });

              requestAnimationFrame(() => {
                const nextRowButton = table
                  .getVirtualizationContainer()
                  ?.querySelector<HTMLButtonElement>(
                    `[data-rowid="${CSS.escape(
                      nextRow.id,
                    )}"][data-trigger="interactive-row"]`,
                  );
                nextRowButton?.focus();
              });
            }
          }
        }
      }
    },
    ..._mergeProps(focusProps, {
      onFocus: (event: ReactFocusEvent<HTMLElement>) => {
        // only activate row if focus was triggered on the row itself
        if (event.currentTarget !== event.target) {
          return;
        }
        // When a row is initially activated, and you click on it to deactivate it using a pointing device,
        // the `pointerdown` event is called before the `focus` event. This code is needed in order to
        // not re-activate the row again. It also ensures that the focus ring is still correctly
        // moved within the table.
        if (clicked.current) {
          clicked.current = false;
          return;
        }

        if (!isActivated && isAutoActivationEnabled) {
          row.toggleActivated(event);
        }
      },
    }),
  };

  return { rowInteractivityClassName, rowInteractivityProps };
}

/**
 * Defines the user action table feature
 * @internal
 */
export const DataTableV2RowInteractivity: TableFeature = {
  createTable<TData>(table: Table<TData>) {
    let hoveredRow: string | null = null;
    /**
     * Activates a given row for the row interactivity
     */
    table.setActiveRow = (
      updater: Updater<string | null>,
      event?: DataTableV2RowActivationEvent,
    ) => {
      table.options.onActiveRowChange?.(updater, event);
    };

    /**
     * Sets a given row as hovered on the table instance.
     */
    table.setHoveredRow = (rowId: string | null) => {
      if (rowId !== hoveredRow) {
        hoveredRow = rowId;
      }
    };

    /**
     * Gets a given row as hovered on the table instance.
     */
    table.getHoveredRow = () => {
      return hoveredRow;
    };

    /** Defines the event handler for the MouseOver to handle activating and hovering the correct rows. */
    table.getInteractiveRowMouseOverHandler = () => {
      return (event: ReactMouseEvent<HTMLDivElement>) => {
        const { target } = event;

        if (
          table.options.enableRowInteractivity !== true ||
          (target instanceof HTMLElement === false &&
            target instanceof SVGElement === false)
        ) {
          return;
        }

        const row = target.closest('[data-row]');
        const rowId =
          row instanceof HTMLElement &&
          'rowid' in row.dataset &&
          row.dataset.rowid;

        if (rowId && table.getHoveredRow() !== rowId) {
          table.setHoveredRow(rowId);
          removeHoverStyles(row);
          addHoverStyles(row);
        } else if (typeof rowId !== 'string') {
          table.setHoveredRow(null);
          removeHoverStyles(target);
        }
      };
    };

    /** Defines the event handler for the MouseLeave to handle activating and hovering the correct rows. */
    table.getInteractiveRowMouseLeaveHandler = () => {
      return (event: ReactMouseEvent<HTMLDivElement>) => {
        const { target } = event;

        if (
          table.options.enableRowInteractivity !== true ||
          (target instanceof HTMLElement === false &&
            target instanceof SVGElement === false)
        ) {
          return;
        }

        table.setHoveredRow(null);
        removeHoverStyles(target);
      };
    };

    /** Defines the event handler for the Click to handle activating and hovering the correct rows. */
    table.getInteractiveRowClickHandler = () => {
      return (event: ReactMouseEvent<HTMLDivElement>) => {
        const { target } = event;

        if (
          table.options.enableRowInteractivity !== true ||
          (target instanceof HTMLElement === false &&
            target instanceof SVGElement === false)
        ) {
          return;
        }

        const row = target.closest('[role="row"]');

        // TODO: Discuss how we want to handle this here? we cannot only check for the direct cell target, as any inner element would violate this trigger.
        const hitInteractiveElement = checkIfHitInteractiveElement(row, target);

        if (row && !hitInteractiveElement) {
          event.preventDefault();
          event.stopPropagation();
          const rowId =
            row instanceof HTMLElement &&
            'rowid' in row.dataset &&
            row.dataset.rowid;

          if (rowId) {
            // If autoActivation is turned off, we need to manually call the
            // onActiveRowChange. Otherwise, it will be handled by the `.focus` handler on the InteractiveRowElement.
            if (
              table.options.enableAutoActivation === false &&
              row !== target
            ) {
              table.options.onActiveRowChange?.(rowId, event);
            }

            row?.focus();
          }
        }
      };
    };
  },

  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    /**
     * Returns a boolean value if the row can be activated.
     */
    row.getCanActivateRow = () => {
      return table.options.enableRowInteractivity === true;
    };

    /**
     * Returns a boolean value if the row is currently activated.
     */
    row.getIsActivated = () => {
      return table.getState().activeRow === row.id;
    };

    /**
     * Activates the row, if it is not activated.
     * Deactivates the row, if it is currently activated.
     */
    row.toggleActivated = (event?: DataTableV2RowActivationEvent) => {
      table.setActiveRow(row.getIsActivated() ? null : row.id, event);
    };
  },
};

/**
 * Configuration hook for the rowInteractivity in the DataTableV2
 * @internal
 */
export function useRowInteractivity<TData extends DataTableV2RowData>(
  props: DataTableV2RowInteractivityProps,
  options: TableOptions<TData>,
) {
  const [activeRow, setActiveRow] = useControllableState<string | null>({
    value: props.activeRow,
    defaultValue: props.defaultActiveRow,
  });
  const interactiveRows = props.interactiveRows;
  options.enableRowInteractivity =
    interactiveRows !== undefined && interactiveRows !== false;

  if (options.enableRowInteractivity) {
    options.onActiveRowChange = (updater: Updater<string | null>, event) => {
      const nextState = functionalUpdate(
        updater,
        options.state?.activeRow ?? null,
      );

      setActiveRow(nextState);
      props.onActiveRowChange?.(nextState, event);
    };
    options.enableAutoActivation =
      interactiveRows &&
      (interactiveRows === true || interactiveRows.autoActivate);
  }

  // Auto reset the state if the data changes
  const firstRun = useRef(true);
  useEffect(
    () => {
      if (firstRun.current) {
        firstRun.current = false;
        return;
      }

      if (options.enableRowInteractivity) {
        options.onActiveRowChange?.(null);
      }
    },
    // We only want to listen to the individual options parts and not the entire options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.enableRowInteractivity, options.data],
  );

  options.state = {
    ...options.state,
    activeRow: activeRow ?? null,
  };
}

function checkIfHitInteractiveElement(
  row: Element | null,
  target: HTMLElement | SVGElement,
): boolean {
  if (row && row !== target) {
    let currentElement: HTMLElement | SVGElement | null = target;
    do {
      const hitInteractiveElement =
        currentElement?.getAttribute('tabindex') !== null ||
        currentElement?.tagName === 'A' ||
        currentElement?.tagName === 'BUTTON' ||
        currentElement?.tagName === 'LABEL' ||
        currentElement?.tagName === 'INPUT' ||
        currentElement?.tagName === 'SELECT';

      if (hitInteractiveElement) {
        return true;
      }
      currentElement = currentElement?.parentElement;
    } while (currentElement !== row && currentElement !== null);
  }
  return false;
}
